// src/components/files/FileSidebar.tsx
import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  LinearProgress,
  alpha,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudIcon from '@mui/icons-material/Cloud';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';
import { FileManagerSection } from '../../types/files';
import { formatFileSize } from '../../services/fileService';

interface MenuItem {
  id: FileManagerSection;
  labelKey: string;
  icon: React.ReactNode;
}

const FileSidebar: React.FC = () => {
  const { t } = useTranslation();
  const { state, navigateToSection, loadQuota } = useFileManager();
  const { section, quota } = state;

  // Load quota on mount
  React.useEffect(() => {
    loadQuota();
  }, [loadQuota]);

  const menuItems: MenuItem[] = [
    {
      id: 'my-space',
      labelKey: 'fileManager.sidebar.mySpace',
      icon: <FolderIcon />,
    },
    {
      id: 'shared',
      labelKey: 'fileManager.sidebar.sharedWithMe',
      icon: <PeopleIcon />,
    },
    {
      id: 'recent',
      labelKey: 'fileManager.sidebar.recent',
      icon: <AccessTimeIcon />,
    },
    {
      id: 'favorites',
      labelKey: 'fileManager.sidebar.favorites',
      icon: <StarIcon />,
    },
    {
      id: 'trash',
      labelKey: 'fileManager.sidebar.trash',
      icon: <DeleteOutlineIcon />,
    },
  ];

  const handleSectionClick = (sectionId: FileManagerSection) => {
    navigateToSection(sectionId);
  };

  const storagePercentage = quota?.storage?.usage_percentage ?? 0;
  const usedStorage = quota?.storage?.total_used_bytes ?? 0;
  const maxStorage = quota?.storage?.max_bytes ?? 0;

  return (
    <Box
      sx={{
        width: 260,
        borderRight: '1px solid',
        borderColor: 'grey.200',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Navigation */}
      <Box sx={{ flex: 1, py: 2 }}>
        <List disablePadding>
          {menuItems.map((item) => {
            const isActive = section === item.id;
            return (
              <ListItem key={item.id} disablePadding sx={{ px: 1.5, mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleSectionClick(item.id)}
                  sx={{
                    borderRadius: 2,
                    py: 1.25,
                    bgcolor: isActive ? alpha('#2563eb', 0.08) : 'transparent',
                    color: isActive ? 'primary.main' : 'text.secondary',
                    '&:hover': {
                      bgcolor: isActive ? alpha('#2563eb', 0.12) : 'grey.100',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isActive ? 'primary.main' : 'grey.500',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(item.labelKey, item.id)}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Storage Quota */}
      <Divider />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <CloudIcon sx={{ fontSize: 20, color: 'grey.500', mr: 1 }} />
          <Typography variant="subtitle2" color="text.secondary">
            {t('fileManager.sidebar.storage', 'Stockage')}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={Math.min(storagePercentage, 100)}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'grey.200',
            mb: 1,
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              bgcolor: storagePercentage > 90 ? 'error.main' : storagePercentage > 70 ? 'warning.main' : 'primary.main',
            },
          }}
        />

        <Typography variant="caption" color="text.secondary">
          {formatFileSize(usedStorage)} / {formatFileSize(maxStorage)}
        </Typography>

        {storagePercentage > 90 && (
          <Typography
            variant="caption"
            color="error.main"
            sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}
          >
            {t('fileManager.sidebar.storageAlmostFull', 'Stockage presque plein')}
          </Typography>
        )}

        {quota?.is_premium && (
          <Box
            sx={{
              mt: 1.5,
              px: 1,
              py: 0.5,
              bgcolor: alpha('#f59e0b', 0.1),
              borderRadius: 1,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 600 }}>
              Premium
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FileSidebar;
